/**
 * GraphControls Component - Zoom, Pan, Reset, Layout Controls
 * Baseado em best practices 2025: AG Charts, Plotly, Grafana patterns
 *
 * Features:
 * - Zoom In/Out buttons
 * - Fit to screen (reset zoom)
 * - Center view
 * - Export PNG
 * - Layout switcher (opcional)
 *
 * Referências:
 * - https://www.ag-grid.com/charts/react/zoom/
 * - https://grafana.com/whats-new/2025-07-31-canvas-pan-and-zoom-improvements/
 */

import {
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingOutIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { Core } from 'cytoscape';
import { Button } from '../ui/Button';

interface GraphControlsProps {
  cyRef: React.MutableRefObject<Core | null>;
  onFit?: () => void;
  onResetZoom?: () => void;
  onExportPNG?: () => string | null;
  className?: string;
}

/**
 * Controles do grafo (zoom, pan, reset, export)
 *
 * Features:
 * - ✅ Zoom in/out programático
 * - ✅ Fit graph (ajustar à tela)
 * - ✅ Reset zoom (voltar para 100%)
 * - ✅ Export PNG (download automático)
 * - ✅ Mobile-friendly (botões empilhados)
 *
 * @example
 * const { cyRef, fitGraph, resetZoom, exportPNG } = useGraph();
 *
 * <GraphControls
 *   cyRef={cyRef}
 *   onFit={fitGraph}
 *   onResetZoom={resetZoom}
 *   onExportPNG={exportPNG}
 * />
 */
export function GraphControls({
  cyRef,
  onFit,
  onResetZoom,
  onExportPNG,
  className = '',
}: GraphControlsProps) {

  /**
   * Zoom in - aumenta zoom em 20%
   */
  const handleZoomIn = () => {
    if (!cyRef.current) return;

    const currentZoom = cyRef.current.zoom();
    cyRef.current.animate({
      zoom: currentZoom * 1.2,
    }, {
      duration: 200,
      easing: 'ease-in-out-cubic',
    });
  };

  /**
   * Zoom out - diminui zoom em 20%
   */
  const handleZoomOut = () => {
    if (!cyRef.current) return;

    const currentZoom = cyRef.current.zoom();
    cyRef.current.animate({
      zoom: currentZoom * 0.8,
    }, {
      duration: 200,
      easing: 'ease-in-out-cubic',
    });
  };

  /**
   * Export PNG - baixa imagem do grafo
   */
  const handleExport = () => {
    if (!onExportPNG) return;

    const pngData = onExportPNG();
    if (!pngData) return;

    // Criar link de download
    const link = document.createElement('a');
    link.href = pngData;
    link.download = `grafo-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`flex flex-col space-y-2 ${className}`}>
      {/* Zoom In */}
      <Button
        variant="secondary"
        size="sm"
        onClick={handleZoomIn}
        icon={<MagnifyingGlassPlusIcon className="h-5 w-5" />}
        className="w-full sm:w-auto"
        aria-label="Zoom in"
        title="Aumentar zoom"
      >
        <span className="sm:hidden">Zoom +</span>
        <span className="hidden sm:inline">Zoom In</span>
      </Button>

      {/* Zoom Out */}
      <Button
        variant="secondary"
        size="sm"
        onClick={handleZoomOut}
        icon={<MagnifyingGlassMinusIcon className="h-5 w-5" />}
        className="w-full sm:w-auto"
        aria-label="Zoom out"
        title="Diminuir zoom"
      >
        <span className="sm:hidden">Zoom -</span>
        <span className="hidden sm:inline">Zoom Out</span>
      </Button>

      {/* Fit to Screen */}
      <Button
        variant="secondary"
        size="sm"
        onClick={onFit}
        icon={<ArrowsPointingOutIcon className="h-5 w-5" />}
        className="w-full sm:w-auto"
        aria-label="Ajustar à tela"
        title="Ajustar grafo à tela"
      >
        <span className="sm:hidden">Ajustar</span>
        <span className="hidden sm:inline">Fit Screen</span>
      </Button>

      {/* Reset Zoom */}
      <Button
        variant="secondary"
        size="sm"
        onClick={onResetZoom}
        icon={<ArrowPathIcon className="h-5 w-5" />}
        className="w-full sm:w-auto"
        aria-label="Resetar zoom"
        title="Resetar zoom para 100%"
      >
        <span className="sm:hidden">Reset</span>
        <span className="hidden sm:inline">Reset Zoom</span>
      </Button>

      {/* Divider */}
      <div className="border-t border-gray-300 my-2" />

      {/* Export PNG */}
      {onExportPNG && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExport}
          icon={<ArrowDownTrayIcon className="h-5 w-5" />}
          className="w-full sm:w-auto"
          aria-label="Exportar PNG"
          title="Baixar grafo como imagem PNG"
        >
          <span className="sm:hidden">Exportar</span>
          <span className="hidden sm:inline">Export PNG</span>
        </Button>
      )}
    </div>
  );
}

/**
 * Floating Controls - Versão posicionada absolutamente sobre o grafo
 * Comum em mapas e visualizações (estilo Google Maps)
 */
interface FloatingGraphControlsProps extends GraphControlsProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

export function FloatingGraphControls({
  position = 'top-right',
  className = '',
  ...controlsProps
}: FloatingGraphControlsProps) {
  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
  };

  return (
    <div
      className={`absolute z-10 ${positionClasses[position]} bg-white rounded-lg shadow-lg p-2 ${className}`}
    >
      <GraphControls {...controlsProps} />
    </div>
  );
}
